Px.Editor.Main = class Main extends Px.Editor.BaseComponent {

  template() {
    const mode = this.store.ui.editor_mode;
    let template;
    if (mode === 'mobile') {
      template = this.mobile_mode_template();
    } else {
      template = this.desktop_mode_template();
    }
    return Px.template`
      <div>
        ${this.font_stylesheets_template()}
        ${template}
      </div>
    `;
  }

  desktop_mode_template() {
    const r = this.renderChild;

    return Px.template`
      <div class="px-editor px-${this.store.ui.editor_mode}-mode px-${Px.config.rtl ? 'rtl' : 'ltr'}-mode
                  ${this.store.cut_print_mode ? 'px-cut-print-mode' : ''} ${this.isCartMode() ? 'px-cart-mode' : ''}">
        ${r(Px.Editor.Header, 'header', this.headerProps)}
        ${Px.if(!this.store.ui.preview_mode, () => {
          return Px.template`
            <div class="px-sidebar px-gallery-sidebar">
              ${r(Px.Editor.ImageImportPanel, 'import-images')}
            </div>
          `;
        })}
        <div class="px-main-area"
             data-has-page-navigation="${this.showPageNavigation}">
          ${Px.if(this.mappedPreviewMode, () => {
            return r(Px.Editor.MappedPreviewPanel, 'mapped-preview-panel');
          }).else(() => {
            return r(Px.Editor.PageDisplay, 'page-display');
          })}
          ${this.showPageNavigation ? r(Px.Editor.PageNavigation, 'page-navigation') : ''}
          ${(this.store.main_view === 'organize-pages') ? r(Px.Editor.OrganizePages, 'organize-pages') : ''}
          ${this.store.cut_print_mode ? r(Px.Editor.CutPrints, 'cut-prints') : ''}
        </div>
        ${Px.if(!this.store.ui.preview_mode, () => {
          return Px.template`
            <div class="px-sidebar px-inspector-sidebar">
              ${r(Px.Editor.VerticalTabbedMenu, 'main-menu', this.tabbedMenuProps)}
            </div>
          `;
        })}
      </div>
    `;
  }

  mobile_mode_template() {
    const r = this.renderChild;

    return Px.template`
      <div class="px-editor px-mobile-mode ${this.store.cut_print_mode ? 'px-cut-print-mode' : ''}
                  px-${Px.config.rtl ? 'rtl' : 'ltr'}-mode
                  ${this.store.cut_print_mode ? 'px-cut-print-mode' : ''}
                  ${this.isCartMode() ? 'px-cart-mode' : ''}"
           data-is-animating="${this.store.mobile.animation_in_progress}"
           data-view-mode="${this.store.mobile.view_mode}"
           data-active-tool="${this.store.mobile.active_tool || ''}"
           data-sidebar-active="${this.store.mobile.sidebar_active}"
           style="height:${this.store.ui.window_height}px">
        <div class="px-notifications">
          ${this.store.notifications.notifications.map(notification => {
            return r(Px.Editor.Notification, `notification-${notification.id}`, {notification: notification});
          })}
        </div>
        <div class="px-main-area">
          ${r(Px.Editor.MobileHeader, 'mobile-header', this.mobileHeaderProps)}
          ${Px.if(this.store.cut_print_mode, () => {
            return r(Px.Editor.CutPrints, 'mobile-cut-prints');
          }).elseIf(!this.store.skip_mobile_project_mode, () => {
            return r(Px.Editor.MobilePageList, 'mobile-page-list');
          })}
          ${r(Px.Editor.MobilePageDisplay, 'mobile-page-display')}
          ${r(Px.Editor.MobileAutofillPanel, 'mobile-autofill-panel')}
          ${r(Px.Editor.MobileToolbar, 'mobile-toolbar')}
        </div>
        ${r(Px.Editor.MobileSidebarPanel, 'mobile-sidebar-panel', this.mobileSidebarPanelProps)}
      </div>
    `;
  }

  // We use a dedicated global container for for text element font-family definition stylesheets.
  // Alternatively the definitions could be embedded inside individual SVG text
  // elements using <defs><style>...</style></defs>, but that completely kills performance
  // in Chrome, which recalculates styles and layout immediately after each individualy
  // text element is appended to the DOM in that case.
  font_stylesheets_template() {
    return Px.template`
      <div>
        ${this.fontDefinitions.map(definition => {
          return Px.template`
            <style type="text/css" id="font-${definition.name}">
              @font-face {
                font-family: '${definition.name}';
                src: url(data:font/truetype;charset=utf-8;base64,${definition.data}) format('truetype');
              }
            </style>
          `;
        })}
      </div>
    `;
  }

  constructor(data) {
    super(data);

    const browser = new UAParser().getBrowser();
    if (browser.name === 'IE') {
      const message = Px.t(
        'browser not supported message',
        'Your browser is not supported. Please use a recent version of Firefox, Chrome, Edge, or Safari.'
      );
      this.makeModal(Px.Components.BaseModal, {
        title: Px.t('Browser not supported'),
        content: message,
        close_on_background_click: false,
        render_close_button: false
      });
      throw new Error(`Browser not supported: ${browser.name}`);
    }

    this.editorModeListener = this.editorModeListener.bind(this);
    this.setWindowDimensions = this.setWindowDimensions.bind(this);
    this.showTooltip = this.showTooltip.bind(this);
    this.disableNativeDrop = this.disableNativeDrop.bind(this);
    this.disableIOSPinchToZoom = this.disableIOSPinchToZoom.bind(this);
    this.onBeforeUnload = this.onBeforeUnload.bind(this);
    this.togglePreviewMode = this.togglePreviewMode.bind(this);
    this.showHelp = this.showHelp.bind(this);
    this.quicksaveProjectModal = this.quicksaveProjectModal.bind(this);
    this.saveProjectModal = this.saveProjectModal.bind(this);
    this.exitWithSaveModal = this.exitWithSaveModal.bind(this);
    this.addToCart = this.addToCart.bind(this);
    this.onTabExpanded = this.onTabExpanded.bind(this);

    Px.configure({
      advanced_edit_mode: (data.mode === 'advanced')
    });

    this.store = Px.Editor.MainStore.make({
      project_id: data.project_id,
      theme_id: data.theme_id,
      resource_type: data.resource_type,
      integration: Px.urlQuery().integration
    });

    this.initEditorModeListeners();
    this.addEditorModeListener(mode => {
      this.store.ui.setEditorMode(mode);
    });

    this.setWindowDimensions();

    // Font family definitions for text elements can change while the element is being manipulated
    // (for example resized). This causes font data to be reloaded from the server and in the meantime
    // the text cannot be shown, unless we keep a cached copy of most recently loaded font data for
    // each individual text element in the project.
    this._cached_font_definitions = {};

    this.registerReaction(() => this.store.ui.editor_mode, mode => {
      if (mode === 'mobile') {
        document.addEventListener('touchmove', this.disableIOSPinchToZoom, {passive: false});
      } else {
        document.removeEventListener('touchmove', this.disableIOSPinchToZoom, {passive: false});
      }
    }, {
      fireImmediately: true,
      name: 'Px.Editor.Main::pinchToZoomWorkaroundReaction'
    });

    this._exiting_without_save = false;

    this._quicksave_prompt_timeout = null;
    this._save_modal = null;
    this._quicksave_modal = null;

    this.registerReaction(
      () => [this.store.quicksave_prompt_delay, this.store.project.has_unsaved_changes],
      ([delay, has_changes]) => {
        if (delay && has_changes) {
          if (!this._quicksave_prompt_timeout) {
            this._quicksave_prompt_timeout = setTimeout(() => {
              if (!this._save_modal) {
                this.quicksaveProjectModal();
              }
            }, delay);
          }
        } else {
          if (this._quicksave_prompt_timeout) {
            clearTimeout(this._quicksave_prompt_timeout);
            this._quicksave_prompt_timeout = null;
          }
        }
      }, {
        name: 'Px.Editor.Main::quicksavePopupReaction'
      }
    )

    window.addEventListener('resize', this.setWindowDimensions);
    window.addEventListener('orientationchange', this.setWindowDimensions);
    window.addEventListener('mousemove', this.showTooltip);
    window.addEventListener('dragover', this.disableNativeDrop);
    window.addEventListener('drop', this.disableNativeDrop);
    window.addEventListener('beforeunload', this.onBeforeUnload);
  }

  destroy() {
    this.removeEditorModelListeners();
    clearTimeout(this._quicksave_prompt_timeout);
    document.removeEventListener('touchmove', this.disableIOSPinchToZoom, {passive: false});
    window.removeEventListener('resize', this.setWindowDimensions);
    window.removeEventListener('orientationchange', this.setWindowDimensions);
    window.removeEventListener('mousemove', this.showTooltip);
    window.removeEventListener('dragover', this.disableNativeDrop);
    window.removeEventListener('drop', this.disableNativeDrop);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    super.destroy();
  }

  static get computedProperties() {
    return {
      tabbedMenuProps: function() {
        return {
          expanded_tab_id: this.store.ui.expanded_tab_id,
          onTabExpanded: this.onTabExpanded,
          tab_groups: this.tabGroups
        };
      },
      tabGroups: function() {
        const store = this.store;
        // Since displaying all tabs by default and then hiding some of them if we find out we are in cut print mode
        // causes annoying flashing and blinking, do not display these tabs initially, and only display them once
        // we are sure we are *not* in cut print mode.
        const maybe_cut_print_mode = !store.theme.loaded || store.cut_print_mode;
        const group_definitions = [
          [
            {
              id: 'view_settings',
              icon: Main.icons.view_settings,
              title: Px.t('View Settings'),
              component: Px.Editor.ViewSettingsPanel,
              data: {store: store},
              hidden: !Px.config.advanced_edit_mode
            },
            {
              id: 'images',
              icon: Main.icons.images,
              title: Px.t('Images'),
              component: Px.Editor.ImagesPanel,
              data: {store: store},
              hidden: maybe_cut_print_mode && !store.selected_set
            },
            {
              id: 'texts',
              icon: Main.icons.text,
              title: Px.t('Text'),
              component: Px.Editor.TextsPanel,
              data: {store: store},
              hidden: maybe_cut_print_mode && !Px.config.advanced_edit_mode
            },
            {
              id: 'admin_elements',
              icon: Main.icons.admin_elements,
              title: Px.t('Elements'),
              component: Px.Editor.AdminElementsPanel,
              data: {store: store},
              hidden: !Px.config.advanced_edit_mode
            },
            {
              id: 'layouts',
              icon: Main.icons.layouts,
              title: Px.t('Layouts'),
              component: Px.Editor.LayoutsPanel,
              data: {store: store},
              hidden: !this.showLayoutsTab
            },
            {
              id: 'background',
              icon: Main.icons.background,
              title: Px.t('Background'),
              component: Px.Editor.BackgroundsPanel,
              data: {store: store},
              hidden: ((!Px.config.background_colors &&
                        store.backgrounds.images.length === 0 &&
                        !Px.config.advanced_edit_mode) ||
                       (maybe_cut_print_mode && !Px.config.advanced_edit_mode) ||
                       store.resource_type === 'theme_layouts')
            },
            {
              id: 'mask',
              icon: Main.icons.mask,
              title: Px.t('Mask'),
              component: Px.Editor.PageMaskPanel,
              data: {store: store},
              hidden: (!Px.config.advanced_edit_mode ||
                       store.resource_type === 'theme_layouts' ||
                       (store.galleries.theme_resources.images.length === 0 &&
                        store.project.pages.every(page => page.mask === null)))
            },
            {
              id: 'options',
              icon: Main.icons.options,
              title: Px.t('Options'),
              component: Px.Editor.OptionsPanel,
              data: {store: store},
              hidden: (!Px.config.project_options ||
                       store.options.combined_available_options.length === 0 ||
                       (maybe_cut_print_mode && store.selected_set) ||
                       store.resource_type !== 'book')
            },
            {
              id: 'page_settings',
              icon: Main.icons.options,
              title: Px.t('Settings'),
              component: Px.Editor.PageSettingsPanel,
              data: {store: store, page: store.selected_page},
              hidden: (!(Px.config.advanced_edit_mode && store.selected_page) ||
                       store.resource_type === 'book')
            }
          ]
        ];
        const groups = [];
        group_definitions.forEach(group => {
          const items = [];
          group.forEach(item => {
            if (!item.hidden) {
              items.push(item);
            }
          });
          if (items.length) {
            groups.push(items);
          }
        });
        return groups;
      },
      headerProps: function() {
        return {
          save_icon: Main.icons.save,
          homepage_url: this.homepageTarget(),
          cart_callback: this.addToCart,
          help_icon: Main.icons.help,
          exit_callback: this.exitWithSaveModal,
          save_callback: this.saveProjectModal,
          preview_mode_callback: this.togglePreviewMode,
          help_callback: this.showHelp
        };
      },
      mobileHeaderProps: function() {
        return {
          cart_icon: Main.icons.cart,
          cart_callback: this.addToCart
        };
      },
      mobileSidebarPanelProps: function() {
        return {
          cart_icon: Main.icons.cart,
          cart_callback: this.addToCart,
          save_icon: Main.icons.save,
          save_callback: this.saveProjectModal,
          exit_icon: Main.icons.exit,
          exit_callback: this.exitWithSaveModal,
          help_icon: Main.icons.help,
          help_callback: this.showHelp,
          options_icon: Main.icons.options
        };
      },
      mappedPreviewMode: function() {
        return this.store.has_mapped_preview && this.store.ui.preview_mode;
      },
      showLayoutsTab: function() {
        const options_store = this.store.options;
        const layouts = this.store.layouts.layouts;
        return this.store.project.editor_page_sets.some(set => {
          return set.pages.some(page => {
            return layouts.some(l => l.applicableToPage(page, options_store));
          });
        });
      },
      showPageNavigation: function() {
        const store = this.store;
        return !store.is_single_page_product && !store.ui.preview_mode && !store.cut_print_mode;
      },
      fontDefinitions: function() {
        // We run through all text elements in the project, and prepare two maps:
        // 1. a map of text element unique ID -> {name: font_name, data: font_data}
        // 2. a map of font name => font data, which has the nice property of removing any duplicates.
        const cached_font_defs = {};
        const unique_font_defs = {};
        const text_store = this.store.texts;
        const text_elements = this.store.project.texts.concat(this.store.layouts.text_elements);
        text_elements.forEach(text => {
          const model = text_store.get(text.json_query);
          if (model && model.loaded) {
            const font = model.data.font;
            unique_font_defs[font.name] = font.data;
            cached_font_defs[text.unique_id] = {name: font.name, data: font.data};
          } else {
            const cached_font = this._cached_font_definitions[text.unique_id];
            if (cached_font) {
              unique_font_defs[cached_font.name] = cached_font.data;
              cached_font_defs[text.unique_id] = cached_font;
            }
          }
        });
        // Cache font properties for text elements for next time we recalculate styles.
        this._cached_font_definitions = cached_font_defs;
        const definitions = [];
        _.each(unique_font_defs, (data, name) => {
          definitions.push({data: data, name: name});
        });
        return definitions;
      }
    };
  }

  isCartMode() {
    return Px.urlQuery().cart === 't';
  }

  homepageTarget() {
    const target = Px.urlQuery().homepage_target || Px.config.homepage_url || '/';
    return target.replace(/\{\{book_id\}\}/g, this.store.project_id);
  }

  saveTarget() {
    let target = Px.urlQuery().save_target;
    if (!target) {
      if (this.store.resource_type !== 'book') {
        target = `/admin/print_theme/theme/${this.store.theme_id}`;
      } else {
        const account_page = Px.config.account_page_name || 'account';
        target = `/site/${account_page}`;
      }
    }
    target = target.replace(/\{\{book_id\}\}/g, this.store.project_id);
    return target;
  }

  defaultCartTarget() {
    const cart_page = Px.config.cart_page_name || 'cart';
    return `/site/${cart_page}`;
  }

  // This is sometimes customized on specific sites, for example
  // https://design.paperchasepress.com/site/editor_custom.js
  cartTarget() {
    let target = Px.urlQuery().cart_target;
    if (!target) {
      target = this.defaultCartTarget();
    } else {
      // These parameters are used for backwards compatibility when a custom cart_target is used.
      target = target.replace(/\{\{book_id\}\}/g, this.store.project_id);
      target = target.replace(/\{\{quantity}\}/g, this.store.quantity);
      if (Px.urlQuery().parent_orderline) {
        target += `&parent_orderline=${Px.urlQuery().parent_orderline}`;
      }
    }
    return target;
  }

  exitTarget() {
    let target = Px.urlQuery().exit_target;
    if (!target) {
      if (this.isCartMode()) {
        target = this.cartTarget();
      } else if (this.store.resource_type !== 'book') {
        target = `/admin/print_theme/theme/${this.store.theme_id}`;
      } else {
        target = this.homepageTarget();
      }
    }
    target = target.replace(/\{\{book_id\}\}/g, this.store.project_id);
    return target;
  }

  saveProject(callback) {
    const store = this.store;
    const local_images = store.project.local_images;
    const local_pdfs = store.project.local_pdfs;
    if (local_images.length || local_pdfs.length) {
      const failed_image = local_images.find(image => {
        return image.image.data.local_file && image.image.data.local_file.upload_failure;
      });
      const failed_pdf = local_pdfs.find(pdf => {
        return pdf.pdf.data.local_file && pdf.pdf.data.local_file.upload_failure;
      });
      if (failed_image) {
        this.makeModal(Px.Editor.Main.BrokenImageModal, {image: failed_image, store: this.store});
      } else if (failed_pdf) {
        this.makeModal(Px.Editor.Main.BrokenImageModal, {pdf: failed_pdf, store: this.store});
      } else {
        this.makeModal(Px.Editor.Main.UploadsInProgressModal, {store: this.store, retry: () => {
          this.saveProject(callback);
        }});
      }
    } else {
      const modal = this.makeModal(Px.Editor.SpinnerModal, {message: Px.t('Saving project...')});
      store.saveProject().then(function() {
        store.showNotification(Px.t('Project successfully saved.'), 'success');
        modal.close();
        if (callback) {
          callback(true);
        }
      }).catch(function(e) {
        store.showNotification(Px.t('Project failed to save.'), 'error');
        modal.close();
        if (callback) {
          callback(false);
        }
      });
    }
  }

  saveAndContinue() {
    const selected_page = this.store.selected_page;
    const selected_set = this.store.selected_set;
    this.saveProject(success => {
      mobx.runInAction(() => {
        if (selected_set) {
          this.store.selectSetById(selected_set.id);
        }
        if (selected_page) {
          this.store.selectPageById(selected_page.id);
        }
      });
    });
  }

  exitWithSaveModal() {
    if (this.store.project.has_unsaved_changes) {
      this.exitProjectModal();
    } else {
      this.exitEditor();
    }
  }

  saveAndExit() {
    this.saveProject(success => {
      if (success) {
        this.exitEditor(this.saveTarget());
      }
    });
  }

  exitEditor(target_page, skip_gallery_check) {
    if (!skip_gallery_check) {
      if (this.store.galleries.project.local_images.length > 0) {
        this.uploadProjectImagesModal(target_page);
        return;
      }
    }

    this._exiting_without_save = true;

    if (!target_page) {
      if (this.store.external_integration.closeEditor) {
        this.store.external_integration.closeEditor(this.store);
        return;
      }
      target_page = this.exitTarget();
    }

    target_page = target_page.replace(/\{\{book_id\}\}/, this.store.project_id);
    this.goToUrl(target_page);
  }

  goToUrl(url) {
    const modal = this.makeModal(Px.Editor.SpinnerModal, {message: Px.t('Redirecting...')});
    const previous_location = window.location.toString();

    setTimeout(() => {
      if (this.store.external_integration.goToUrl) {
        this.store.external_integration.goToUrl(this.store, url);
      } else {
        window.location = url;
      }

      // Moving to a different page could be cancelled by a beforeunload handler.
      // If the document is in complete state, that probably means that moving to another URL
      // was cancelled, so remove the modal.
      // The 1000 value is kind of random and this thing doesn't always work, but I can't find
      // any more reliable way to do this. One way to work around the issue would be to unhook
      // the beforeunload event and use a custom confirmation dialog when using goToUrl,
      // but that seems like a lot of work for such a silly feature.
      setTimeout(() => {
        if (window.location.toString() === previous_location) {
          modal.close();
        }
      }, 1000);
    });
  }

  // -------
  // Actions
  // -------

  load() {
    var self = this;
    this.store.load().then(function() {
      $j(window).trigger('resize');
    }).catch(function(error_message) {
      self.makeModal(Px.Components.BaseModal, {
        title: Px.t('Error loading the project'),
        content: error_message,
        close_on_background_click: false,
        render_close_button: false
      });
    });
  }

  // --------------
  // Event handlers
  // --------------

  onBeforeUnload(evt) {
    if (this.store.project.has_unsaved_changes && !this._exiting_without_save) {
      var message = Px.t('Project has unsaved changes. Are you sure you want to leave without saving?');
      evt.returnValue = message;
      return message;
    }
  }

  togglePreviewMode() {
    return this.store.ui.togglePreviewMode();
  }

  showHelp() {
    const url = (Px.config.help_url || '').trim();
    if (url) {
      if (url.startsWith('javascript:')) {
        new Function(url.replace(/^javascript:/, ''))();
      } else if (this.store.ui.editor_mode === 'mobile') {
        window.open(url, '_blank');
      } else {
        this.makeModal(Px.Components.BaseModal, {
          content: Px.raw(`<iframe src="${url}" />`),
          css_class: 'px-help-modal'
        });
      }
    }
  }

  quicksaveProjectModal() {
    this._quicksave_modal = this.makeModal(Px.Editor.QuicksaveProjectModal, {
      saveAndContinue: this.saveAndContinue.bind(this)
    });
    this._quicksave_modal.on('destroy', () => {
      this._quicksave_modal = null;
    });
  }

  saveProjectModal() {
    this._save_modal = this.makeModal(Px.Editor.SaveProjectModal, {
      saveAndContinue: this.saveAndContinue.bind(this),
      saveAndExit: this.saveAndExit.bind(this)
    });
    this._save_modal.on('destroy', () => {
      this._save_modal = null;
    });
  }

  exitProjectModal() {
    this._save_modal = this.makeModal(Px.Editor.ExitProjectModal, {
      exitWithoutSaving: this.exitEditor.bind(this),
      saveAndExit: this.saveAndExit.bind(this)
    });
    this._save_modal.on('destroy', () => {
      this._save_modal = null;
    });
  }

  uploadProjectImagesModal(exit_target) {
    this._save_modal = this.makeModal(Px.Editor.UploadProjectImagesModal, {
      store: this.store,
      exitEditor: this.exitEditor.bind(this, exit_target, true)
    });
    this._save_modal.on('destroy', () => {
      this._save_modal = null;
    });
  }

  addSavedProjectToCart() {
    if (this.cartTarget() !== this.defaultCartTarget()) {
      // This is for backwards compatibility for sites that use a custom cart_target URL
      // to add projects to the cart (for example on external sites such as SPS).
      // The new default way to add projects to the cart from the editor is to POST to the
      // /cart/add_project endpoint, which then redirects to the cart page.
      this.goToUrl(this.cartTarget());
    } else {
      const fields = {
        print_book_id:  this.store.project_id,
        quantity: this.store.quantity,
        target: this.cartTarget()
      };
      if (Px.urlQuery().parent_orderline) {
        fields.parent_orderline_id = Px.urlQuery().parent_orderline;
      }
      const form = document.createElement('form');
      form.setAttribute('action', '/cart/add_print_product');
      form.setAttribute('method', 'post');
      form.hidden = true;
      Object.keys(fields).forEach(key => {
        const input = document.createElement('input');
        input.setAttribute('name', key);
        input.value = fields[key];
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    }
  }

  addToCart() {
    if (this.store.cut_print_mode) {
      const total_prints = this.store.total_cut_print_quantity;
      const min_prints = this.store.theme.min_cut_print_quantity;
      if (total_prints < min_prints) {
        let content = Px.t('Minimum order quantity is {{min_count}} prints. Please add {{remaining_count}} more.');
        content = content.replace('{{min_count}}', min_prints);
        content = content.replace('{{remaining_count}}', min_prints - total_prints);
        this.makeModal(Px.Components.BaseModal, {
          title: Px.t('Add more prints'),
          content: content,
          footer: Px.template`
            <button class="px-large px-strong px-primary-color" data-onclick="close">
              ${Px.t('OK')}
            </button>
          `,
          css_class: 'px-confirmation-modal'
        });
        return;
      }
    }

    const saveAndAddToCart = () => {
      this.saveProject(success => {
        if (success) {
          if (this.store.external_integration.addToCart) {
            this.store.external_integration.addToCart(this.store);
          } else if (this.isCartMode()) {
            this.goToUrl(this.cartTarget());
          } else {
            this.addSavedProjectToCart();
          }
        }
      });
    };

    if (Px.config.unedited_warning) {
      const unedited_elements = this.store.project.unedited_elements;
      if (unedited_elements.length) {
        this.makeModal(Px.Editor.UneditedElementsModal, {
          saveAndContinue: saveAndAddToCart,
          cancel: () => {
            mobx.runInAction(() => {
              this.store.selectSet(unedited_elements[0].page.set);
              this.store.selectElement(unedited_elements[0]);
            });
          }
        });
      } else {
        saveAndAddToCart();
      }
    } else {
      saveAndAddToCart();
    }
  }

  onTabExpanded(tab_id) {
    if (tab_id === this.store.ui.expanded_tab_id) {
      this.store.ui.expandTab(null);
    } else {
      this.store.ui.expandTab(tab_id);
    }
  }

  setWindowDimensions(evt) {
    cancelAnimationFrame(this.__resize_raf);
    this.__resize_raf = requestAnimationFrame(() => {
      const width = $j(window).width();
      const height = $j(window).height();
      // Workaround for crappy vh unit support on iOS.
      document.documentElement.style.setProperty('--detected-window-height', `${height}px`);
      const ui_store = this.store.ui;
      mobx.runInAction(() => {
        ui_store.window_width = width;
        ui_store.window_height = height;
      });
    });
  }

  showTooltip(evt) {
    // Don't show tooltips while dragging, etc.
    if (evt.buttons) {
      this.hideTooltip();
      return;
    }

    let element = evt.target.closest && evt.target.closest('[data-px-tooltip]');
    if (!element) {
      // If target is a SVG element, and it doesn't have a tooltip,
      // we need to drill down to find any other tooltips obscured by the current element.
      const coords = Px.Util.pointerCoords(evt);
      const nodes =  document.elementsFromPoint(coords.clientX, coords.clientY)
      for (const node of nodes) {
        if (node.namespaceURI !== 'http://www.w3.org/2000/svg') {
          break;
        }
        element = node.closest('[data-px-tooltip]');
        if (element) {
          break;
        }
      }
    }
    const current_tooltip = this.current_tooltip;
    if (current_tooltip) {
      if (current_tooltip.owner_element === element) {
        // Already the active tooltip, nothing to do here.
        return;
      } else {
        this.hideTooltip();
      }
    }
    if (element) {
      const div = document.createElement('div');
      div.className = 'px-tooltip';
      div.innerText = element.getAttribute('data-px-tooltip');
      this.current_tooltip = div;
      this.current_tooltip.owner_element = element;
      document.body.appendChild(this.current_tooltip);
      const offset = parseInt(element.getAttribute('data-px-tooltip-offset') || 4, 10);
      const owner_rect = element.getBoundingClientRect();
      const owner_left = owner_rect.left + window.scrollX;
      const owner_top = owner_rect.top + window.scrollY;
      const owner_bottom = owner_rect.bottom + window.scrollY;
      let left = owner_left + (owner_rect.width / 2) - (div.clientWidth / 2);
      let top = owner_bottom + offset;
      // Make sure the tooltip doesn't overflow the page.
      if (left < 0) {
        left = 0;
      } else if (left + div.clientWidth > document.body.clientWidth) {
        left = document.body.clientWidth - div.clientWidth;
      }
      // Tooltip is positioned below the element by default, but if it overflows the page,
      // position it above the element instead.
      if (top + div.clientHeight > document.body.clientHeight) {
        top = owner_top - (div.clientHeight + offset);
      }
      div.style.top = top + 'px';
      div.style.left = left + 'px';
    }
  }

  // Prevents browser from replacing the loaded window with the image if user
  // drags and drops an image file from the desktop into the editor somewhere outside
  // any valid drop areas.
  disableNativeDrop(evt) {
    evt.preventDefault();
  }

  // Disables pinch-to-zoom iOS gesture that scales the page up.
  disableIOSPinchToZoom(evt) {
    // We don't want to disable all native touch gestures such as scrolling,
    // so only prevent default if evt.scale is different from 1;
    // evt.scale is a custom Apple property, so this only works on iOS.
    // On android touch-action: none should work (but we need to test that).
    if (typeof evt.scale === 'number' && evt.scale !== 1) {
      evt.preventDefault();
    }
  }

  // -------
  // Private
  // -------

  initEditorModeListeners() {
    this._editor_mode_callbacks = [];

    this._editor_mode_media_queries = {
      mobile: Px.Util.matchMedia('mobile')
    };

    _.each(this._editor_mode_media_queries, media_query => {
      media_query.addListener(this.editorModeListener);
    });
  }

  removeEditorModelListeners() {
    _.each(this._editor_mode_media_queries, media_query => {
      media_query.removeListener(this.editorModeListener);
    });
  }

  getEditorMode() {
    let mode;
    const url_query = Px.urlQuery();
    if (url_query.editor_mode) {
      mode = url_query.editor_mode;
    } else if (this._editor_mode_media_queries.mobile.matches) {
      mode = 'mobile';
    } else {
      mode = 'desktop';
    }
    return mode;
  }

  editorModeListener() {
    const mode = this.getEditorMode();
    this._editor_mode_callbacks.forEach(callback => callback(mode));
  }

  addEditorModeListener(callback) {
    this._editor_mode_callbacks.push(callback);
    callback(this.getEditorMode());
  }

  hideTooltip() {
    if (this.current_tooltip) {
      this.current_tooltip.owner_element = null;
      this.current_tooltip.remove();
      this.current_tooltip = null;
    }
  }

};

Px.Editor.Main.BrokenImageModal = class BrokenImageModal extends Px.Components.BaseModal {

  get css_class() {
    return 'px-broken-image-modal';
  }

  get title() {
    return Px.t('Project failed to save');
  }

  get content() {
    let message;

    if (this.data.image) {
      message = Px.t(
        'image upload error message',
        'Image "{{image_name}}" used on page "{{page_name}}" failed to upload.<br/>' +
          'Please replace the broken image and try again.'
      );
      const image = this.data.image;
      message = message.replace('{{image_name}}', image.image.data.file.name);
      message = message.replace('{{page_name}}', image.page.caption);
    } else {
      message = Px.t(
        'pdf upload error message',
        'PDF "{{pdf_name}}" used on page "{{page_name}}" failed to upload.<br/>' +
          'Please replace the broken PDF and try again.'
      );
      const pdf = this.data.pdf;
      message = message.replace('{{pdf_name}}', pdf.pdf.data.file.name);
      message = message.replace('{{page_name}}', pdf.page.caption);
    }
    return Px.raw(message);
  }

  get footer() {
    return Px.template`
      <button class="px-large px-strong px-primary-color" data-onclick="goToElement">
        ${Px.t('Go to Image')}
      </button>
    `;
  }

  get dataProperties() {
    return {
      image: {required: false},
      pdf: {required: false},
      store: {required: true}
    };
  }

  static get computedProperties() {
    return {
      broken_element: function() {
        return this.data.image || this.data.pdf;
      }
    };
  }

  // --------------
  // Event handlers
  // --------------

  goToElement(evt) {
    evt.preventDefault();
    const store = this.data.store;
    mobx.runInAction(() => {
      store.selectSet(this.broken_element.page.set);
      store.selectElement(this.broken_element);
      this.close();
    });
  }
};

Px.Editor.Main.UploadsInProgressModal = class UploadsInProgressModal extends Px.Components.BaseModal {

  get css_class() {
    return `${super.css_class} px-confirmation-modal px-uploads-in-progress-modal`;
  }

  get title() {
    return Px.t('Image uploads in progress')
  }

  get content() {
    return Px.template`
      <p>${Px.t('Some images are still uploading. Please wait...')}</p>
      <div class="px-upload-progress">
        <div class="px-upload-progress-bar" style="width:${this.uploadProgress}%"></div>
      </div>
      <p class="px-upload-progress-details">
        ${this.uploadProgress}%
      </p>
    `;
  }

  get footer() {
    return Px.template`
      <button data-onclick="close" class="px-cancel">
        ${Px.t('Cancel')}
      </button>
      <button data-onclick="retry" class="px-ok" ${this.disabledAttribute}>
        ${Px.t('Continue')}
      </button>
    `;
  }

  constructor(props) {
    super(props);
    this.registerReaction(
      () => {
        return this.inProgressLocalFiles.length === 0;
      },
      finished => {
        if (finished) {
          this.retry();
        }
      },
      {name: 'Px.Editor.Main.UploadsInProgressModal::retryOnAllUploadedReaction'}
    );
  }

  get dataProperties() {
    return {
      retry: {required: true},
      store: {required: true}
    };
  }

  static get computedProperties() {
    return {
      uploadedImages: function() {
        const images = this.data.store.project.images.map(image_element => image_element.image);
        return _.uniq(images).filter(image => {
          return image.type === 'db' && this.data.store.images.uploadedAsLocalFileThisSession(image);
        });
      },
      uploadedPdfs: function() {
        const pdfs = this.data.store.project.pdfs.map(pdf_element => pdf_element.pdf);
        return _.uniq(pdfs).filter(pdf => {
          return pdf.type === 'db' && this.data.store.pdfs.uploadedAsLocalFileThisSession(pdf);
        });
      },
      inProgressLocalFiles: function() {
        const local_image_elements = this.data.store.project.local_images;
        const local_image_files = _.uniq(local_image_elements.map(image_element => image_element.image.data.local_file));
        const local_pdf_elements = this.data.store.project.local_pdfs;
        const local_pdf_files = _.uniq(local_pdf_elements.map(pdf_element => pdf_element.pdf.data.local_file));
        return local_image_files.concat(local_pdf_files);
      },
      totalCount: function() {
        return this.uploadedImages.length + this.uploadedPdfs.length + this.inProgressLocalFiles.length;
      },
      uploadProgress: function() {
        let progress = this.uploadedImages.length + this.uploadedPdfs.length;
        this.inProgressLocalFiles.forEach(file => {
          if (!file.upload_failure) {
            progress += (file.upload_progress_percent / 100);
          }
        });
        return Math.round((progress / this.totalCount) * 100);
      },
      disabledAttribute: function() {
        return this.inProgressLocalFiles.length > 0 ? 'disabled' : '';
      }
    };
  }

  // --------------
  // Event handlers
  // --------------

  retry(evt) {
    this.data.retry();
    this.close();
  }

};


Px.Editor.Main.icons = {
  view_settings: '<svg width="23" height="16" viewBox="0 0 576 512"><path d="M64 0C28.7 0 0 28.7 0 64V352c0 35.3 28.7 64 64 64H240l-10.7 32H160c-17.7 0-32 14.3-32 32s14.3 32 32 32H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H346.7L336 416H512c35.3 0 64-28.7 64-64V64c0-35.3-28.7-64-64-64H64zM512 64V352H64V64H512z" fill="currentColor"/></svg>',
  images: '<svg width="23" height="23" viewBox="0 0 23 23" fill="none"><path d="M4.79167 10.6375L6.70833 8.72083L11.9792 13.9917L15.3333 10.6375L18.2083 13.5125V4.79167H4.79167V10.6375ZM3.83333 2.875H19.1667C19.4208 2.875 19.6646 2.97597 19.8443 3.15569C20.024 3.33541 20.125 3.57917 20.125 3.83333V19.1667C20.125 19.4208 20.024 19.6646 19.8443 19.8443C19.6646 20.024 19.4208 20.125 19.1667 20.125H3.83333C3.57917 20.125 3.33541 20.024 3.15569 19.8443C2.97597 19.6646 2.875 19.4208 2.875 19.1667V3.83333C2.875 3.57917 2.97597 3.33541 3.15569 3.15569C3.33541 2.97597 3.57917 2.875 3.83333 2.875ZM14.8542 9.58333C14.4729 9.58333 14.1073 9.43188 13.8377 9.1623C13.5681 8.89272 13.4167 8.52708 13.4167 8.14583C13.4167 7.76458 13.5681 7.39895 13.8377 7.12937C14.1073 6.85978 14.4729 6.70833 14.8542 6.70833C15.2354 6.70833 15.601 6.85978 15.8706 7.12937C16.1402 7.39895 16.2917 7.76458 16.2917 8.14583C16.2917 8.52708 16.1402 8.89272 15.8706 9.1623C15.601 9.43188 15.2354 9.58333 14.8542 9.58333Z" fill="currentColor"/></svg>',
  text: '<svg width="23" height="23" viewBox="0 0 23 23" fill="none"><path d="M3.60661 18.582H20.4002C20.73 18.582 20.9999 18.8543 20.9999 19.1871V20.3972C20.9999 20.73 20.73 21.0023 20.4002 21.0023H3.60661C3.27673 21.0023 3.00684 20.73 3.00684 20.3972V19.1871C3.00684 18.8543 3.27673 18.582 3.60661 18.582Z" fill="currentColor"/><path d="M3.60661 13.1348H20.4002C20.73 13.1348 20.9999 13.407 20.9999 13.7398V14.9499C20.9999 15.2827 20.73 15.555 20.4002 15.555H3.60661C3.27673 15.555 3.00684 15.2827 3.00684 14.9499V13.7398C3.00684 13.407 3.27673 13.1348 3.60661 13.1348Z" fill="currentColor"/><path d="M14.2824 7.68848H20.4C20.7299 7.68848 20.9998 7.96075 20.9998 8.29353V9.50365C20.9998 9.83643 20.7299 10.1087 20.4 10.1087H14.2824C13.9525 10.1087 13.6826 9.83643 13.6826 9.50365V8.29353C13.6826 7.96075 13.9525 7.68848 14.2824 7.68848Z" fill="currentColor"/><path d="M11.2837 10.2909L8.43479 3.21177C8.37481 3.09076 8.25486 3 8.10491 3H5.94574C5.82579 3 5.67585 3.09076 5.64586 3.21177L3.00688 10.2909C2.97689 10.4119 3.03686 10.5935 3.21679 10.5935H4.59626C4.71622 10.5935 4.86616 10.4725 4.89615 10.3514L5.43594 8.8388H8.76466L9.36443 10.3514C9.39442 10.4725 9.54436 10.5935 9.66431 10.5935H11.0438C11.2237 10.5935 11.3137 10.4422 11.2837 10.2909ZM6.0657 7.02363L6.93536 4.78492H7.11529L8.07493 7.02363H6.0657Z" fill="currentColor"/></svg>',
  admin_elements: '<svg width="26px" height="26px" viewBox="0 0 26 26"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"><g transform="translate(-1123.000000, -179.000000)" stroke="currentColor" stroke-width="2"><g transform="translate(1096.000000, 0.000000)"><g transform="translate(0.000000, 160.000000)"><g transform="translate(28.000000, 20.000000)"><polygon points="0 0 12 0 12 12 0 12"></polygon><polygon points="16 16 24 16 24 24 16 24"></polygon></g></g></g></g></g></svg>',
  layouts: '<svg width="23" height="23" viewBox="0 0 23 23" fill="none"><path d="M10.5417 2.875V20.125H3.83333C3.57917 20.125 3.33541 20.024 3.15569 19.8443C2.97597 19.6646 2.875 19.4208 2.875 19.1667V3.83333C2.875 3.57917 2.97597 3.33541 3.15569 3.15569C3.33541 2.97597 3.57917 2.875 3.83333 2.875H10.5417ZM20.125 12.4583V19.1667C20.125 19.4208 20.024 19.6646 19.8443 19.8443C19.6646 20.024 19.4208 20.125 19.1667 20.125H12.4583V12.4583H20.125ZM19.1667 2.875C19.4208 2.875 19.6646 2.97597 19.8443 3.15569C20.024 3.33541 20.125 3.57917 20.125 3.83333V10.5417H12.4583V2.875H19.1667Z" fill="currentColor"/></svg>',
  background: '<svg width="25" height="25" viewBox="0 0 25 25" fill="none"><path d="M18.7109 18.1716C19.7778 18.1716 20.6494 17.2878 20.6494 16.2062C20.6494 14.9001 18.7109 12.7687 18.7109 12.7687C18.7109 12.7687 16.7725 14.9001 16.7725 16.2062C16.7725 17.2878 17.644 18.1716 18.7109 18.1716ZM9.50439 17.0949C9.67773 17.2682 9.9585 17.2682 10.1294 17.0949L16.3818 10.8449C16.5552 10.6716 16.5552 10.3908 16.3818 10.2199L10.1318 3.96989C10.1172 3.95524 10.1001 3.94059 10.083 3.92839L8.17383 2.01921C8.1322 1.97812 8.07607 1.95508 8.01758 1.95508C7.95909 1.95508 7.90295 1.97812 7.86133 2.01921L6.68945 3.19108C6.64836 3.23271 6.62532 3.28884 6.62532 3.34733C6.62532 3.40582 6.64836 3.46196 6.68945 3.50358L8.33008 5.14421L3.25684 10.2199C3.0835 10.3932 3.0835 10.674 3.25684 10.8449L9.50439 17.0949ZM9.81934 6.14274L14.187 10.5104H5.4541L9.81934 6.14274ZM22.0703 19.922H2.92969C2.82227 19.922 2.73438 20.0099 2.73438 20.1174V22.0705C2.73438 22.1779 2.82227 22.2658 2.92969 22.2658H22.0703C22.1777 22.2658 22.2656 22.1779 22.2656 22.0705V20.1174C22.2656 20.0099 22.1777 19.922 22.0703 19.922Z" fill="currentColor"/></svg>',
  options: '<svg width="23" height="23" viewBox="0 0 23 23" fill="none"><path d="M18.8425 8.32504C18.1929 8.32688 17.5597 8.53128 17.0298 8.91022C16.4999 9.28916 16.0993 9.8241 15.8827 10.4417H2.04956C1.7712 10.4417 1.50424 10.5532 1.30741 10.7517C1.11058 10.9501 1 11.2193 1 11.5C1 11.7807 1.11058 12.0499 1.30741 12.2483C1.50424 12.4468 1.7712 12.5583 2.04956 12.5583H15.8827C16.0753 13.1075 16.4139 13.5926 16.8616 13.9607C17.3093 14.3287 17.8487 14.5655 18.4209 14.6452C18.9931 14.7248 19.576 14.6441 20.1058 14.4121C20.6356 14.18 21.0919 13.8055 21.4248 13.3294C21.7576 12.8534 21.9541 12.2942 21.9929 11.713C22.0317 11.1319 21.9111 10.5512 21.6445 10.0345C21.3779 9.51782 20.9755 9.08504 20.4813 8.78353C19.9871 8.48202 19.4201 8.3234 18.8425 8.32504ZM2.04956 6.20839H3.28804C3.50866 6.82132 3.91108 7.35089 4.44062 7.72513C4.97016 8.09938 5.60114 8.30014 6.24779 8.30014C6.89444 8.30014 7.52541 8.09938 8.05496 7.72513C8.5845 7.35089 8.98692 6.82132 9.20754 6.20839H20.9416C21.22 6.20839 21.4869 6.09689 21.6838 5.89842C21.8806 5.69994 21.9912 5.43075 21.9912 5.15007C21.9912 4.86939 21.8806 4.6002 21.6838 4.40172C21.4869 4.20325 21.22 4.09175 20.9416 4.09175H9.20754C8.98692 3.47882 8.5845 2.94925 8.05496 2.57501C7.52541 2.20076 6.89444 2 6.24779 2C5.60114 2 4.97016 2.20076 4.44062 2.57501C3.91108 2.94925 3.50866 3.47882 3.28804 4.09175H2.04956C1.7712 4.09175 1.50424 4.20325 1.30741 4.40172C1.11058 4.6002 1 4.86939 1 5.15007C1 5.43075 1.11058 5.69994 1.30741 5.89842C1.50424 6.09689 1.7712 6.20839 2.04956 6.20839ZM20.9416 16.7916H13.4058C13.1852 16.1787 12.7827 15.6491 12.2532 15.2749C11.7236 14.9006 11.0927 14.6999 10.446 14.6999C9.79937 14.6999 9.1684 14.9006 8.63886 15.2749C8.10932 15.6491 7.70689 16.1787 7.48627 16.7916H2.04956C1.7712 16.7916 1.50424 16.9031 1.30741 17.1016C1.11058 17.3001 1 17.5692 1 17.8499C1 18.1306 1.11058 18.3998 1.30741 18.5983C1.50424 18.7968 1.7712 18.9083 2.04956 18.9083H7.48627C7.70689 19.5212 8.10932 20.0508 8.63886 20.425C9.1684 20.7992 9.79937 21 10.446 21C11.0927 21 11.7236 20.7992 12.2532 20.425C12.7827 20.0508 13.1852 19.5212 13.4058 18.9083H20.9416C21.22 18.9083 21.4869 18.7968 21.6838 18.5983C21.8806 18.3998 21.9912 18.1306 21.9912 17.8499C21.9912 17.5692 21.8806 17.3001 21.6838 17.1016C21.4869 16.9031 21.22 16.7916 20.9416 16.7916Z" fill="currentColor"/></svg>',
  undo: '<svg width="26px" height="22px" viewBox="0 0 26 22"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round"><g transform="translate(-1123.000000, -506.000000)" stroke="currentColor" stroke-width="2"><g transform="translate(1096.000000, 0.000000)"><g transform="translate(0.000000, 440.000000)"><g transform="translate(0.000000, 45.000000)"><g transform="translate(28.000000, 22.000000)"><polyline stroke-linejoin="round" points="0 7 4 11 8 7"></polyline><path d="M14,20 C19.5228475,20 24,15.5228475 24,10 C24,4.4771525 19.5228475,0 14,0 C8.4771525,0 4,4.4771525 4,10"></path></g></g></g></g></g></g></svg>',
  redo: '<svg width="26px" height="22px" viewBox="0 0 26 22"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round"><g transform="translate(-1123.000000, -586.000000)" stroke="currentColor" stroke-width="2"><g transform="translate(1096.000000, 0.000000)"><g transform="translate(0.000000, 440.000000)"><g transform="translate(0.000000, 125.000000)"><g transform="translate(40.000000, 32.000000) scale(-1, 1) translate(-40.000000, -32.000000) translate(28.000000, 22.000000)"><polyline stroke-linejoin="round" points="0 7 4 11 8 7"></polyline><path d="M14,20 C19.5228475,20 24,15.5228475 24,10 C24,4.4771525 19.5228475,0 14,0 C8.4771525,0 4,4.4771525 4,10"></path></g></g></g></g></g></g></svg>',
  preview: '<svg width="26px" height="26px" viewBox="0 0 26 26"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"><g transform="translate(-1123.000000, -665.000000)" stroke="currentColor" stroke-width="2"><g transform="translate(1096.000000, 0.000000)"><g transform="translate(0.000000, 440.000000)"><g transform="translate(0.000000, 205.000000)"><g transform="translate(28.000000, 21.000000)"><path d="M24,12 C24,18.6281379 18.6268966,24 12,24 C5.37144828,24 0,18.6281379 0,12 C0,5.37227586 5.37144828,0 12,0 C18.6268966,0 24,5.37227586 24,12 L24,12 Z"></path><polygon points="9 17 9 7 17 11.9997797"></polygon></g></g></g></g></g></g></svg>',
  share: '<svg width="26px" height="26px" viewBox="0 0 26 26"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g transform="translate(-1122.000000, -803.000000)" stroke-width="2" stroke="currentColor"><g transform="translate(1096.000000, 0.000000)"><g transform="translate(0.000000, 784.000000)"><g transform="translate(27.000000, 20.000000)"><path d="M16,6 L8,11"></path><path d="M16,14 L8,19" transform="translate(12.000000, 16.500000) scale(-1, 1) translate(-12.000000, -16.500000) "></path><path d="M8,12 C8,14.2088 6.2092,16 4,16 C1.7908,16 0,14.2088 0,12 C0,9.7912 1.7908,8 4,8 C6.2092,8 8,9.7912 8,12 L8,12 Z"></path><path d="M24,4 C24,6.2088 22.2092,8 20,8 C17.7908,8 16,6.2088 16,4 C16,1.7912 17.7908,0 20,0 C22.2092,0 24,1.7912 24,4 L24,4 Z"></path><path d="M24,20 C24,22.2088 22.2092,24 20,24 C17.7908,24 16,22.2088 16,20 C16,17.7912 17.7908,16 20,16 C22.2092,16 24,17.7912 24,20 L24,20 Z"></path></g></g></g></g></g></svg>',
  cart: '<svg width="26px" height="24px" viewBox="0 0 26 24"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"><g transform="translate(-1123.000000, -964.000000)" stroke="currentColor" stroke-width="2"><g transform="translate(1096.000000, 0.000000)"><g transform="translate(0.000000, 784.000000)"><g transform="translate(0.000000, 160.000000)"><g transform="translate(28.000000, 21.000000)"><path d="M5,20 C5,21.1045 4.1045,22 3,22 C1.8955,22 1,21.1045 1,20 C1,18.8955 1.8955,18 3,18 C4.1045,18 5,18.8955 5,20 L5,20 Z"></path><path d="M18,20 C18,21.1045 17.1045,22 16,22 C14.8955,22 14,21.1045 14,20 C14,18.8955 14.8955,18 16,18 C17.1045,18 18,18.8955 18,20 L18,20 Z"></path><polyline points="24 0 21 0 17 18 3 18"></polyline><polyline points="17.7267048 14 2 14 0 3 20 3"></polyline></g></g></g></g></g></g></svg>',
  save: '<svg width="26px" height="26px" viewBox="0 0 26 26"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"><g transform="translate(-1123.000000, -883.000000)" stroke="currentColor" stroke-width="2"><g transform="translate(1096.000000, 0.000000)"><g transform="translate(0.000000, 784.000000)"><g transform="translate(0.000000, 80.000000)"><g transform="translate(28.000000, 20.000000)"><polygon points="3 0 0 4 0 24 24 24 24 0"></polygon><polygon points="19 4 5 4 5 0 19 0"></polygon><polygon points="5 24 19 24 19 20 5 20"></polygon><path d="M16,12 C16,14.2088 14.2088,16 12,16 C9.7908,16 8,14.2088 8,12 C8,9.7912 9.7908,8 12,8 C14.2088,8 16,9.7912 16,12 L16,12 Z"></path></g></g></g></g></g></g></svg>',
  mask: '<svg width="26px" height="26px" viewBox="0 0 26 26"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"><g transform="translate(-1123.000000, -179.000000)" stroke="currentColor" stroke-width="2"><g transform="translate(1096.000000, 0.000000)"><g transform="translate(0.000000, 160.000000)"><g transform="translate(28.000000, 20.000000)"><polygon points="24 20 20 24 4 24 0 20 0 4 4 0 20 0 24 4"></polygon></g></g></g></g></g></svg>',
  help: '<svg width="26px" height="26px" viewBox="0 0 26 26"><path d="M25,13A12,12,0,1,1,13,1,12,12,0,0,1,25,13Z" transform="translate(0 0)" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2px"/><path d="M11.45,16v-.62a3.59,3.59,0,0,1,.4-1.79,5,5,0,0,1,1.36-1.47,7.46,7.46,0,0,0,1.5-1.43,2.1,2.1,0,0,0,.34-1.21,1.5,1.5,0,0,0-.55-1.26,2.47,2.47,0,0,0-1.57-.44,5.59,5.59,0,0,0-1.72.26,12.19,12.19,0,0,0-1.54.64L8.85,7A8.52,8.52,0,0,1,13.1,5.9a4.61,4.61,0,0,1,3,.93,3.19,3.19,0,0,1,1.12,2.59A3.83,3.83,0,0,1,17,10.73a3.58,3.58,0,0,1-.65,1.08,10,10,0,0,1-1.5,1.35,5.88,5.88,0,0,0-1.21,1.18,2.26,2.26,0,0,0-.31,1.26V16Zm-.4,3.12a1.29,1.29,0,0,1,1.44-1.48,1.39,1.39,0,0,1,1.07.39,1.5,1.5,0,0,1,.38,1.09,1.56,1.56,0,0,1-.38,1.1,1.39,1.39,0,0,1-1.07.4,1.38,1.38,0,0,1-1.06-.39A1.57,1.57,0,0,1,11.05,19.16Z" transform="translate(0 0)" fill="currentColor"/></svg>',
  caret_left: '<svg width="12px" height="24px" viewBox="0 0 12 24"><polyline stroke-linejoin="round" stroke-linecap="round" stroke-width="2" stroke="#8492A6" fill="none" points="11 1, 1 12, 11 23"/></svg>',
  plus: '<svg width="24" height="24" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23" stroke-width="2" stroke="#1FB6FF" /><line x1="1" y1="12" x2="23" y2="12" stroke-width="2" stroke="#1FB6FF" /></svg>',
  cross: '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.579987 0.579742C0.790925 0.369067 1.07686 0.250732 1.37499 0.250732C1.67311 0.250732 1.95905 0.369067 2.16999 0.579742L6.99999 5.40974L11.83 0.579742C11.933 0.469212 12.0572 0.380559 12.1952 0.319071C12.3332 0.257583 12.4821 0.22452 12.6332 0.221855C12.7843 0.21919 12.9343 0.246977 13.0744 0.303558C13.2145 0.36014 13.3417 0.444356 13.4485 0.551185C13.5554 0.658013 13.6396 0.785264 13.6962 0.925346C13.7528 1.06543 13.7805 1.21547 13.7779 1.36653C13.7752 1.51758 13.7421 1.66655 13.6807 1.80455C13.6192 1.94255 13.5305 2.06675 13.42 2.16974L8.58999 6.99974L13.42 11.8297C13.5305 11.9327 13.6192 12.0569 13.6807 12.1949C13.7421 12.3329 13.7752 12.4819 13.7779 12.633C13.7805 12.784 13.7528 12.9341 13.6962 13.0741C13.6396 13.2142 13.5554 13.3415 13.4485 13.4483C13.3417 13.5551 13.2145 13.6393 13.0744 13.6959C12.9343 13.7525 12.7843 13.7803 12.6332 13.7776C12.4821 13.775 12.3332 13.7419 12.1952 13.6804C12.0572 13.6189 11.933 13.5303 11.83 13.4197L6.99999 8.58974L2.16999 13.4197C1.95672 13.6185 1.67465 13.7266 1.3832 13.7215C1.09175 13.7164 0.813674 13.5983 0.607554 13.3922C0.401435 13.1861 0.283367 12.908 0.278224 12.6165C0.273082 12.3251 0.381267 12.043 0.579987 11.8297L5.40999 6.99974L0.579987 2.16974C0.369311 1.9588 0.250977 1.67287 0.250977 1.37474C0.250977 1.07662 0.369311 0.79068 0.579987 0.579742Z" fill="currentColor"/></svg>',
  exit: '<svg width="26" height="16" viewBox="0 0 14 14" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.579987 0.579742C0.790925 0.369067 1.07686 0.250732 1.37499 0.250732C1.67311 0.250732 1.95905 0.369067 2.16999 0.579742L6.99999 5.40974L11.83 0.579742C11.933 0.469212 12.0572 0.380559 12.1952 0.319071C12.3332 0.257583 12.4821 0.22452 12.6332 0.221855C12.7843 0.21919 12.9343 0.246977 13.0744 0.303558C13.2145 0.36014 13.3417 0.444356 13.4485 0.551185C13.5554 0.658013 13.6396 0.785264 13.6962 0.925346C13.7528 1.06543 13.7805 1.21547 13.7779 1.36653C13.7752 1.51758 13.7421 1.66655 13.6807 1.80455C13.6192 1.94255 13.5305 2.06675 13.42 2.16974L8.58999 6.99974L13.42 11.8297C13.5305 11.9327 13.6192 12.0569 13.6807 12.1949C13.7421 12.3329 13.7752 12.4819 13.7779 12.633C13.7805 12.784 13.7528 12.9341 13.6962 13.0741C13.6396 13.2142 13.5554 13.3415 13.4485 13.4483C13.3417 13.5551 13.2145 13.6393 13.0744 13.6959C12.9343 13.7525 12.7843 13.7803 12.6332 13.7776C12.4821 13.775 12.3332 13.7419 12.1952 13.6804C12.0572 13.6189 11.933 13.5303 11.83 13.4197L6.99999 8.58974L2.16999 13.4197C1.95672 13.6185 1.67465 13.7266 1.3832 13.7215C1.09175 13.7164 0.813674 13.5983 0.607554 13.3922C0.401435 13.1861 0.283367 12.908 0.278224 12.6165C0.273082 12.3251 0.381267 12.043 0.579987 11.8297L5.40999 6.99974L0.579987 2.16974C0.369311 1.9588 0.250977 1.67287 0.250977 1.37474C0.250977 1.07662 0.369311 0.79068 0.579987 0.579742Z" fill="currentColor"/></svg>',
  organize_pages: '<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M11.6667 8.24992V6.83325C12.125 6.63881 12.5939 6.49297 13.0734 6.39575C13.5528 6.29853 14.0562 6.24992 14.5834 6.24992C14.9445 6.24992 15.2987 6.2777 15.6459 6.33325C15.9931 6.38881 16.3334 6.45825 16.6667 6.54159V7.87492C16.3334 7.74992 15.9964 7.65603 15.6559 7.59325C15.3153 7.53047 14.9578 7.49936 14.5834 7.49992C14.0556 7.49992 13.5487 7.56603 13.0625 7.69825C12.5764 7.83047 12.1112 8.01436 11.6667 8.24992ZM11.6667 12.8333V11.4166C12.125 11.2221 12.5939 11.0763 13.0734 10.9791C13.5528 10.8819 14.0562 10.8333 14.5834 10.8333C14.9445 10.8333 15.2987 10.861 15.6459 10.9166C15.9931 10.9721 16.3334 11.0416 16.6667 11.1249V12.4583C16.3334 12.3333 15.9964 12.2394 15.6559 12.1766C15.3153 12.1138 14.9578 12.0827 14.5834 12.0833C14.0556 12.0833 13.5487 12.1458 13.0625 12.2708C12.5764 12.3958 12.1112 12.5833 11.6667 12.8333ZM11.6667 10.5416V9.12492C12.125 8.93047 12.5939 8.78464 13.0734 8.68742C13.5528 8.5902 14.0562 8.54158 14.5834 8.54158C14.9445 8.54158 15.2987 8.56936 15.6459 8.62492C15.9931 8.68047 16.3334 8.74992 16.6667 8.83325V10.1666C16.3334 10.0416 15.9964 9.9477 15.6559 9.88492C15.3153 9.82214 14.9578 9.79103 14.5834 9.79158C14.0556 9.79158 13.5487 9.8577 13.0625 9.98992C12.5764 10.1221 12.1112 10.306 11.6667 10.5416ZM5.41671 13.3333C6.06949 13.3333 6.70504 13.4063 7.32337 13.5524C7.94171 13.6985 8.55615 13.9171 9.16671 14.2083V5.99992C8.59726 5.66658 7.9931 5.41659 7.35421 5.24992C6.71532 5.08325 6.06949 4.99992 5.41671 4.99992C4.91671 4.99992 4.42004 5.04853 3.92671 5.14575C3.43337 5.24297 2.95782 5.38881 2.50004 5.58325V13.8333C2.98615 13.6666 3.46893 13.5416 3.94837 13.4583C4.42782 13.3749 4.91726 13.3333 5.41671 13.3333ZM10.8334 14.2083C11.4445 13.9166 12.0589 13.6977 12.6767 13.5516C13.2945 13.4055 13.93 13.3327 14.5834 13.3333C15.0834 13.3333 15.5731 13.3749 16.0525 13.4583C16.532 13.5416 17.0145 13.6666 17.5 13.8333V5.58325C17.0417 5.38881 16.5659 5.24297 16.0725 5.14575C15.5792 5.04853 15.0828 4.99992 14.5834 4.99992C13.9306 4.99992 13.2848 5.08325 12.6459 5.24992C12.007 5.41659 11.4028 5.66658 10.8334 5.99992V14.2083ZM10 16.6666C9.33337 16.1388 8.61115 15.7291 7.83337 15.4374C7.0556 15.1458 6.25004 14.9999 5.41671 14.9999C4.83337 14.9999 4.26032 15.0763 3.69754 15.2291C3.13476 15.3819 2.59671 15.5971 2.08337 15.8749C1.79171 16.0277 1.5106 16.0208 1.24004 15.8541C0.969485 15.6874 0.83393 15.4444 0.833374 15.1249V5.08325C0.833374 4.93047 0.871707 4.78464 0.948374 4.64575C1.02504 4.50686 1.13949 4.4027 1.29171 4.33325C1.9306 3.99992 2.59726 3.74992 3.29171 3.58325C3.98615 3.41659 4.69448 3.33325 5.41671 3.33325C6.22226 3.33325 7.0106 3.43742 7.78171 3.64575C8.55282 3.85409 9.29226 4.16659 10 4.58325C10.7084 4.16659 11.4481 3.85409 12.2192 3.64575C12.9903 3.43742 13.7784 3.33325 14.5834 3.33325C15.3056 3.33325 16.0139 3.41659 16.7084 3.58325C17.4028 3.74992 18.0695 3.99992 18.7084 4.33325C18.8612 4.4027 18.9759 4.50686 19.0525 4.64575C19.1292 4.78464 19.1673 4.93047 19.1667 5.08325V15.1249C19.1667 15.4444 19.0314 15.6874 18.7609 15.8541C18.4903 16.0208 18.2089 16.0277 17.9167 15.8749C17.4028 15.5971 16.8648 15.3819 16.3025 15.2291C15.7403 15.0763 15.1673 14.9999 14.5834 14.9999C13.75 14.9999 12.9445 15.1458 12.1667 15.4374C11.3889 15.7291 10.6667 16.1388 10 16.6666Z" fill="currentColor"/></svg>'
};
